/**
 * Workflow Runner — 按 ExecutionPlan queue 顺序执行 Material Workflow
 *
 * 不做 day/platform/taskType 判断，只 dispatch plan items。
 */
import {
  generateMaterialPhoto,
  generateMaterialSilentVideo,
  generateMaterialTalkingVideo,
  generateMaterialText,
  generateLeadHook,
  updateDailyPackItem,
  type TextMaterialType,
} from '@/api/materialLibrary';
import type { SceneType, PhotoSubtype, SilentMotion } from '@/lib/materialScenes';
import type { ExecutionPlan, MaterialPlan, WorkflowRunResult } from './types';
import { materialItemKey } from './materialPlanner';

export interface WorkflowCallbacks {
  onJobStart?: (order: number, type: string, key?: string) => void;
  onJobDone?: (order: number, type: string, ok: boolean, key?: string) => void;
}

async function runPhoto(
  characterId: number,
  item: MaterialPlan['items'][number],
): Promise<void> {
  const count = item.count || 1;
  for (let i = 0; i < count; i++) {
    await generateMaterialPhoto({
      character_id: characterId,
      scene_type: item.scene as SceneType,
      photo_subtype: (item.photoSubtype || 'friend_angle') as PhotoSubtype,
      usage: item.usage,
    });
  }
}

async function runShortVideo(characterId: number, item: MaterialPlan['items'][number]): Promise<void> {
  await generateMaterialSilentVideo({
    character_id: characterId,
    scene_type: item.scene as SceneType,
    motion: (item.motion || 'walk') as SilentMotion,
    usage: item.usage,
  });
}

async function runTalkingVideo(characterId: number): Promise<void> {
  await generateMaterialTalkingVideo({ character_id: characterId });
}

async function runText(characterId: number, item: MaterialPlan['items'][number]): Promise<void> {
  if (!item.textType) return;
  await generateMaterialText(characterId, item.textType as TextMaterialType, item.count);
}

async function runLeadHook(characterId: number): Promise<void> {
  await generateLeadHook(characterId, undefined, { usage: 'dm_icebreaker', topic: '主页检查表引流' });
}

/** 按 queue 顺序执行 workflow；可选 selectedKeys 只跑使用者勾选的项 */
export async function runWorkflowQueue(
  characterId: number,
  plan: MaterialPlan,
  executionPlan: ExecutionPlan,
  packId: string | null,
  callbacks?: WorkflowCallbacks,
  selectedKeys?: string[] | null,
): Promise<WorkflowRunResult> {
  const messages: string[] = [];
  let completedJobs = 0;
  let failedJobs = 0;
  const allow = selectedKeys && selectedKeys.length > 0 ? new Set(selectedKeys) : null;
  const picked = (key: string) => !allow || allow.has(key);

  const mediaItems = plan.items.filter((i) => !i.textType && i.scene !== 'lead_hook');
  const textItems = plan.items.filter((i) => !!i.textType);
  const leadHookItem = plan.items.find((i) => i.scene === 'lead_hook');

  for (const job of executionPlan.queue) {
    const item = mediaItems.find((i) => i.jobType === job.type && i.order === job.order);
    if (!item) continue;
    const key = materialItemKey(item);
    if (!picked(key)) continue;

    callbacks?.onJobStart?.(job.order, job.type, key);
    const reqId = `req_${String(job.order).padStart(3, '0')}`;

    try {
      if (job.type === 'photo') await runPhoto(characterId, item);
      else if (job.type === 'short_video') await runShortVideo(characterId, item);
      else if (job.type === 'talking_video') await runTalkingVideo(characterId);
      else if (job.type === 'voice') await runTalkingVideo(characterId);

      if (packId) await updateDailyPackItem(packId, reqId, 'completed');
      messages.push(`${job.type} 已提交`);
      completedJobs++;
      callbacks?.onJobDone?.(job.order, job.type, true, key);
    } catch (e) {
      if (packId) await updateDailyPackItem(packId, reqId, 'failed', { error: String(e) });
      messages.push(`${job.type} 失败`);
      failedJobs++;
      callbacks?.onJobDone?.(job.order, job.type, false, key);
      if (job.blocking) break;
    }
  }

  for (const item of textItems) {
    const key = materialItemKey(item);
    if (!picked(key)) continue;
    const reqId = item.textType === 'dm_opener' ? 'req_004' : 'req_text_moments';
    callbacks?.onJobStart?.(item.order, item.textType || 'text', key);
    try {
      await runText(characterId, item);
      if (packId && item.textType === 'dm_opener') await updateDailyPackItem(packId, reqId, 'completed');
      messages.push(`${item.textType} 话术已生成`);
      completedJobs++;
      callbacks?.onJobDone?.(item.order, item.textType || 'text', true, key);
    } catch (e) {
      if (packId && item.textType === 'dm_opener') await updateDailyPackItem(packId, reqId, 'failed', { error: String(e) });
      messages.push(`${item.textType} 话术失败`);
      failedJobs++;
      callbacks?.onJobDone?.(item.order, item.textType || 'text', false, key);
    }
  }

  if (leadHookItem) {
    const key = materialItemKey(leadHookItem);
    if (picked(key)) {
      callbacks?.onJobStart?.(leadHookItem.order, 'lead_hook', key);
      try {
        await runLeadHook(characterId);
        if (packId) await updateDailyPackItem(packId, 'req_005', 'completed');
        messages.push('引流钩子已生成');
        completedJobs++;
        callbacks?.onJobDone?.(leadHookItem.order, 'lead_hook', true, key);
      } catch (e) {
        if (packId) await updateDailyPackItem(packId, 'req_005', 'failed', { error: String(e) });
        messages.push('引流钩子失败');
        failedJobs++;
        callbacks?.onJobDone?.(leadHookItem.order, 'lead_hook', false, key);
      }
    }
  }

  return {
    ok: failedJobs === 0,
    message: messages.join('；') || '今日无执行任务',
    completedJobs,
    failedJobs,
  };
}
